import { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Tooltip, styled, keyframes } from "@mui/material";
import { IOpsLinkMessage, IOpsLinkTopic } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { resolveFullFlights } from "src/utils/dispatchDeskOpsLinkSync";

const flashAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
`;

const OCOpsLinkCounter = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  zIndex: 1,
  margin: "0 5px",
  "& .counter-badge": {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    padding: "2px 6px",
    borderRadius: 3,
    backgroundColor: theme.palette.success.light,
    fontSize: 14,
    fontWeight: 300,
    cursor: "pointer",
    "& .emoji": {
      fontSize: 12,
    },
    "&.has-unacked": {
      backgroundColor: theme.palette.warning.main,
      animation: `${flashAnimation} 1.5s ease-in-out infinite`,
    },
  },
}));

export const OpsLinkUnackedCounter = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const flightEvents = useSelector((state: IRootState) => state.OpsLinkReducer.flightEvents);
  const desks = useSelector((state: IRootState) => state.dispatchDesks?.desks);
  const currentUserId = useSelector((state: IRootState) => state.authentication?.attributes?._id);

  const unackedCount = useMemo(() => {
    if (!flightEvents) return 0;

    let count = 0;
    for (const flightId of Object.keys(flightEvents)) {
      const topicEvents = flightEvents[flightId];
      if (!topicEvents) continue;

      for (const topicId of Object.keys(topicEvents)) {
        const messages: IOpsLinkMessage[] = topicEvents[topicId];
        if (!Array.isArray(messages)) continue;

        for (const msg of messages) {
          const topic = msg.topic as IOpsLinkTopic;
          if (!topic?.acknowledgeTracking) continue;

          // Skip messages not from crew — only crew messages need acknowledgement
          if (!msg.fromCrew) continue;

          // Count messages where NO ONE has acknowledged
          if (!msg.acknowledgedBy || msg.acknowledgedBy.length === 0) {
            count++;
          }
        }
      }
    }
    return count;
  }, [flightEvents]);

  const handleClick = useCallback(async () => {
    // Gather all unique flight IDs from the user's dispatch desks
    const userDesks = (desks ?? []).filter((d) => d.users.some((u) => u._id === currentUserId));
    const seen = new Set<string>();
    const flightIds: string[] = [];
    for (const desk of userDesks) {
      for (const f of desk.flights) {
        if (!seen.has(f._id)) {
          seen.add(f._id);
          flightIds.push(f._id);
        }
      }
    }
    if (flightIds.length === 0) return;
    // Resolve full flight objects — uses Redux cache first, only API-fetches missing ones.
    const flights = await resolveFullFlights(flightIds);
    if (flights.length > 0) {
      dispatch(openWindow("OpsLink — My Desks", "OpsLinkWindow", [], { initialFlights: flights }, "content"));
    }
  }, [dispatch, desks, currentUserId]);

  // Compute the number of flights the user is subscribed to via dispatch desks
  const subscribedFlightCount = useMemo(() => {
    const userDesks = (desks ?? []).filter((d) => d.users.some((u) => u._id === currentUserId));
    const seen = new Set<string>();
    for (const desk of userDesks) {
      for (const f of desk.flights) {
        seen.add(f._id);
      }
    }
    return seen.size;
  }, [desks, currentUserId]);

  // Don't render until the user has dispatch desk flights
  if (subscribedFlightCount === 0) return null;

  const tooltipText =
    unackedCount > 0
      ? `${unackedCount} unacknowledged crew message(s) — action required · ${subscribedFlightCount} flight(s) subscribed`
      : `All OpsLink messages acknowledged · ${subscribedFlightCount} flight(s) subscribed`;

  return (
    <OCOpsLinkCounter>
      <Tooltip title={tooltipText}>
        <Grid container className={`counter-badge ${unackedCount > 0 ? "has-unacked" : ""}`} onClick={handleClick}>
          <span className="emoji">📡</span>
          <span>{unackedCount}</span>
        </Grid>
      </Tooltip>
    </OCOpsLinkCounter>
  );
};
